<script>
import { useRoute } from 'vue-router'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'

import { useTokenManager } from '@/utils/authorization/token-manager'
import { cleanupRecaptcha, executeRecaptcha, loadRecaptchaScript } from '@/plugins/recaptcha'
import { AUTH_ACTION, useAuthUtils } from '@/pages/auth/auth-utils'
import { maSessionStorage } from '@/lib/storage'
import { useMaCore } from '@/composables/maCore'
import { strHash, TEST_USER_HASH } from '@/base/MaUtils'
import { maLogger } from '@/base/logger'
import { MAIN_PAGE_ROUTE_NAME, RouteName } from '@/base/constants/route'
import { AUTH_PLATFORM } from '@/base/constants/Auth'

import { MaAlert, MaButton, MaForm, MaFormItem, MaIcon, MaInput, MaLinkButton } from '@mobileaction/action-kit'
import MaTextWithUnderStrike from './components/MaTextWithUnderStrike.vue'
import MaSocialLoginRow from './components/MaSocialLoginRow.vue'

export default {
  beforeRouteEnter(to, from, next) {
    const { isUserLoggedIn, redirectToSearchAds, getPlatform } = useAuthUtils()
    if (isUserLoggedIn()) {
      const platform = getPlatform()
      if (platform == AUTH_PLATFORM.MA)
        return next({ name: MAIN_PAGE_ROUTE_NAME })

      redirectToSearchAds()
    }

    next()
  },
}
</script>

<script setup>
const route = useRoute()
const { maRequests } = useMaCore()
const { processSignInRequest, getPlatform } = useAuthUtils()
const { removeUserAuth } = useTokenManager()

const form = reactive({
  username: null,
  password: null,
  userResponse: null,
  platform: getPlatform(),
})
const loading = ref(false)
const socialLoading = ref(false)
const errorMessage = ref(null)

const rules = computed(() => {
  return {
    username: [
      { required: true, message: 'Email is required' },
      { type: 'email', message: 'Email must be valid', trigger: form.username !== '' ? 'blur' : 'change' },
    ],
    password: [
      { required: true, message: 'Password is required' },
    ],
  }
})

async function handleSubmit() {
  loading.value = true
  errorMessage.value = null
  try {
    if (strHash(form.username) != TEST_USER_HASH) {
      // if test user is not test user, then execute recaptcha.
      form.userResponse = await executeRecaptcha('login')
    }

    removeUserAuth() // Be sure current user is invalidated from cache.
    const userAuth = await maRequests.Authentication.loginWithEmail(form)
    await processSignInRequest(userAuth, AUTH_ACTION.LOGIN, 'email')
  }
  catch (e) {
    maLogger.warn('Login error:', e)
    const { errors = [], message } = e || {}
    if (errors.includes('INVALID_CAPTCHA') || message == 'EXECUTE_RECAPTCHA_FAILED') {
      errorMessage.value = 'Failed to pass captcha challenge, if it persists please contact us.'
    }
    else if (errors.includes('FAILED_LOGIN_LIMIT_REACHED')) {
      errorMessage.value = 'Failed login limit has been reached, please try again later.'
    }
    else {
      errorMessage.value = 'Failed to login, please make sure your email and password are correct.'
    }
  }
  finally {
    loading.value = false
  }
}

function handleSocialLoadingState(state) {
  socialLoading.value = state
}

function handleSocialLoginError(error) {
  errorMessage.value = error
}

onMounted(async () => {
  if (route.query?.forwardTo) {
    maSessionStorage.forwardTo = route.query.forwardTo
  }

  try {
    await loadRecaptchaScript()
  }
  catch (e) {
    maLogger.warn('Failed to load reCaptcha Enterprise:', e)
  }
})

onUnmounted(() => {
  cleanupRecaptcha()
})
</script>

<template>
  <div class="ma-auth-login">
    <h1>Welcome back!</h1>
    <MaAlert
      v-if="errorMessage"
      type="danger"
      variant="filled"
      :message="errorMessage"
      :closable="false"
    />
    <div v-if="socialLoading" class="social-loading">
      <MaIcon name="loader" spin />
      <span>Loading...</span>
    </div>
    <template v-else>
      <MaSocialLoginRow
        :logging-in="loading"
        class="mt-6"
        @error="handleSocialLoginError"
        @loading="handleSocialLoadingState"
      />
      <MaTextWithUnderStrike dark>
        or continue with
      </MaTextWithUnderStrike>
      <MaForm
        :model="form"
        :rules="rules"
        @finish="handleSubmit"
      >
        <MaFormItem name="username">
          <MaInput
            v-model:value="form.username"
            title="Email"
            trim
            placeholder="Enter your email"
            size="large"
            type="email"
            autofocus
          />
        </MaFormItem>
        <MaFormItem name="password" class="item-password">
          <MaInput
            v-model:value="form.password"
            title="Password"
            placeholder="Enter your password"
            type="password"
            size="large"
          >
            <template #hint>
              <div class="input-hints">
                <router-link
                  v-slot="{ href, navigate }"
                  :to="{ name: RouteName.AUTH_LOGIN_SSO, query: $route.query }"
                  custom
                >
                  <MaLinkButton
                    size="small"
                    :href="href"
                    @click="navigate"
                  >
                    Log in with SSO
                  </MaLinkButton>
                </router-link>
                <router-link
                  v-slot="{ href, navigate }"
                  :to="{ name: RouteName.AUTH_RECOVER, query: $route.query }"
                  custom
                >
                  <MaLinkButton
                    size="small"
                    :href="href"
                    @click="navigate"
                  >
                    Forgot Password?
                  </MaLinkButton>
                </router-link>
              </div>
            </template>
          </MaInput>
        </MaFormItem>
        <MaButton
          :loading="loading"
          size="large"
          html-type="submit"
          class="w-full"
        >
          Log in
        </MaButton>
      </MaForm>
    </template>
    <p class="register-redirect">
      Don’t have an account?
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: RouteName.AUTH_REGISTER, query: $route.query }"
        custom
      >
        <MaLinkButton
          color="blue"
          :href="href"
          @click="navigate"
        >
          Create a free account
        </MaLinkButton>
      </router-link>
    </p>
  </div>
</template>

<style lang="scss" scoped>
    .ma-auth-login {
  @apply min-h-full flex flex-col;
  > h1 {
    @apply text-[2rem] leading-[3rem] text-center text-gray-900 font-semibold;
  }
  > .ma-alert {
    @apply mt-4;
  }
  > .social-loading {
    @apply flex items-center justify-center gap-2;
  }
  .ma-text-with-under-strike {
    @apply my-6;
    :deep(.ma-text-content) {
      @apply text-xs text-gray-400;
    }
    :deep(.ma-sep) {
      @apply bg-gray-200;
    }
  }
  form {
    @apply flex flex-col gap-1 mb-4 md:mb-0;
    :deep(.ma-form-item) {
      @apply mb-0;
      .input-hints {
        @apply flex flex-row items-center justify-between;
      }
    }
    :deep(.ak-button--primary) {
      @apply w-full mt-1 mb-4 md:mb-0;
    }
  }
  .register-redirect {
    @apply mt-auto mb-8 md:mb-0 md:mt-4 text-center text-gray-500 font-normal text-sm md:text-xs;
  }
}
</style>
